import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';

const GestionSeccionesLanding = () => {
  const { user, API } = useContext(AuthContext);

  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSeccion, setCurrentSeccion] = useState(null);
  const [uploadingImagen, setUploadingImagen] = useState(false);

  const [formData, setFormData] = useState({
    titulo: '',
    contenido: '',
    imagen_url: '',
    mapa_iframe: '',
    tipo: 'texto',
    orden: 0,
    activo: true
  });

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean']
    ]
  };

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadSecciones();
    }
  }, [user]);

  const loadSecciones = async () => {
    try {
      const response = await axios.get(`${API}/admin/secciones-landing`);
      setSecciones(response.data);
    } catch (error) {
      console.error('Error loading secciones:', error);
      toast.error('Error al cargar secciones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setCurrentSeccion(null);
    setFormData({
      titulo: '',
      contenido: '',
      imagen_url: '',
      mapa_iframe: '',
      tipo: 'texto',
      orden: secciones.length,
      activo: true
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (seccion) => {
    setIsEditing(true);
    setCurrentSeccion(seccion);
    setFormData({
      titulo: seccion.titulo,
      contenido: seccion.contenido,
      imagen_url: seccion.imagen_url || '',
      mapa_iframe: seccion.mapa_iframe || '',
      tipo: seccion.tipo,
      orden: seccion.orden,
      activo: seccion.activo
    });
    setIsDialogOpen(true);
  };

  const handleImagenUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingImagen(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/class-banner`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, imagen_url: response.data.banner_url });
      toast.success('Imagen subida exitosamente');
    } catch (error) {
      console.error('Error uploading imagen:', error);
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImagen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🟢 SECTION FORM SUBMIT TRIGGERED');
    console.log('🔍 Form data:', formData);
    
    // Validación manual
    if (!formData.titulo || formData.titulo.trim().length < 2) {
      toast.error('El título es requerido (mínimo 2 caracteres)');
      return;
    }
    
    if (!formData.contenido || formData.contenido.trim().length < 1) {
      toast.error('El contenido es requerido');
      return;
    }

    console.log('✅ Validación pasada, enviando al backend...');

    try {
      if (isEditing && currentSeccion) {
        console.log('🔄 Updating seccion:', currentSeccion.id);
        await axios.put(`${API}/admin/secciones-landing/${currentSeccion.id}`, formData);
        toast.success('Sección actualizada exitosamente');
      } else {
        console.log('✨ Creating new seccion');
        console.log('📡 API URL:', `${API}/admin/secciones-landing`);
        await axios.post(`${API}/admin/secciones-landing`, formData);
        toast.success('Sección creada exitosamente');
      }

      setIsDialogOpen(false);
      loadSecciones();
    } catch (error) {
      console.error('❌ ERROR COMPLETO:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al guardar sección');
    }
  };

  const handleDelete = async (seccionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sección?')) return;

    try {
      await axios.delete(`${API}/admin/secciones-landing/${seccionId}`);
      toast.success('Sección eliminada exitosamente');
      loadSecciones();
    } catch (error) {
      console.error('Error deleting seccion:', error);
      toast.error('Error al eliminar sección');
    }
  };

  const handleToggleActivo = async (seccion) => {
    try {
      await axios.put(`${API}/admin/secciones-landing/${seccion.id}`, {
        activo: !seccion.activo
      });
      toast.success(`Sección ${!seccion.activo ? 'activada' : 'desactivada'} exitosamente`);
      loadSecciones();
    } catch (error) {
      console.error('Error toggling seccion:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleMoveUp = async (seccion, index) => {
    if (index === 0) return;

    try {
      await axios.put(`${API}/admin/secciones-landing/${seccion.id}/orden`, null, {
        params: { nuevo_orden: seccion.orden - 1 }
      });
      await axios.put(`${API}/admin/secciones-landing/${secciones[index - 1].id}/orden`, null, {
        params: { nuevo_orden: secciones[index - 1].orden + 1 }
      });
      loadSecciones();
    } catch (error) {
      console.error('Error moving seccion:', error);
      toast.error('Error al mover sección');
    }
  };

  const handleMoveDown = async (seccion, index) => {
    if (index === secciones.length - 1) return;

    try {
      await axios.put(`${API}/admin/secciones-landing/${seccion.id}/orden`, null, {
        params: { nuevo_orden: seccion.orden + 1 }
      });
      await axios.put(`${API}/admin/secciones-landing/${secciones[index + 1].id}/orden`, null, {
        params: { nuevo_orden: secciones[index + 1].orden - 1 }
      });
      loadSecciones();
    } catch (error) {
      console.error('Error moving seccion:', error);
      toast.error('Error al mover sección');
    }
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-red-600">Acceso denegado. Solo administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="text-center py-8">
            <i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i>
            <p className="mt-4">Cargando secciones...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-bold">
              <i className="fas fa-layer-group mr-2"></i>
              Gestión de Secciones Landing Page
            </CardTitle>
            <Button onClick={handleCreate} className="instituto-gradient text-white">
              <i className="fas fa-plus mr-2"></i>
              Nueva Sección
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {secciones.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-inbox text-4xl mb-4"></i>
                <p>No hay secciones creadas. Crea la primera sección.</p>
              </div>
            ) : (
              secciones.map((seccion, index) => (
                <Card key={seccion.id} className={`${!seccion.activo ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{seccion.titulo}</h3>
                          <Badge variant={seccion.activo ? 'default' : 'secondary'}>
                            {seccion.activo ? 'Visible' : 'Oculto'}
                          </Badge>
                          <Badge variant="outline">{seccion.tipo}</Badge>
                          <span className="text-sm text-gray-500">Orden: {seccion.orden}</span>
                        </div>
                        <div 
                          className="text-sm text-gray-600 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                        />
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {/* Botones de orden */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMoveUp(seccion, index)}
                            disabled={index === 0}
                            className="h-8 w-8 p-0"
                          >
                            <i className="fas fa-arrow-up text-xs"></i>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMoveDown(seccion, index)}
                            disabled={index === secciones.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            <i className="fas fa-arrow-down text-xs"></i>
                          </Button>
                        </div>

                        {/* Toggle Activo/Inactivo */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActivo(seccion)}
                          className={seccion.activo ? 'text-green-600' : 'text-gray-400'}
                        >
                          <i className={`fas ${seccion.activo ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                        </Button>

                        {/* Editar */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(seccion)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>

                        {/* Eliminar */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(seccion.id)}
                          className="text-red-600"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog para Crear/Editar - DISEÑO MEJORADO */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-blue-50 to-purple-50">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus-square'} text-blue-600`}></i>
              {isEditing ? 'Editar Sección' : 'Nueva Sección'}
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-1">
              {isEditing ? 'Actualiza el contenido de la sección' : 'Crea una nueva sección para el landing page'}
            </p>
          </DialogHeader>
          
          <form id="seccion-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Información Básica */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <Label className="text-lg font-semibold text-gray-800 mb-4 block">
                  <i className="fas fa-info-circle mr-2 text-blue-600"></i>
                  Información Básica
                </Label>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="titulo" className="text-sm font-medium">
                      Título de la Sección <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="titulo"
                      value={formData.titulo}
                      onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                      required
                      placeholder="Ej: Nuestra Misión, Historia del IBA, Visión 2025..."
                      className="mt-1 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="fas fa-lightbulb mr-1"></i>
                      Este será el encabezado principal de la sección
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="tipo" className="text-sm font-medium">
                      Tipo de Sección <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.tipo} onValueChange={(value) => setFormData({ ...formData, tipo: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="texto">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-align-left"></i>
                            <span>Solo Texto</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="texto_imagen">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-columns"></i>
                            <span>Texto con Imagen (2 Columnas)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="imagen">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-image"></i>
                            <span>Solo Imagen</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="ubicacion">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-map-marker-alt"></i>
                            <span>Ubicación con Mapa</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="grid">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-th"></i>
                            <span>Grid de Tarjetas</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="fas fa-info-circle mr-1"></i>
                      Define cómo se mostrará el contenido en el landing page
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contenido */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <Label className="text-lg font-semibold text-gray-800 mb-3 block">
                  <i className="fas fa-file-alt mr-2 text-blue-600"></i>
                  Contenido de la Sección <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Utiliza el editor para dar formato a tu texto. Puedes agregar títulos, listas, negritas, colores, etc.
                </p>
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.contenido}
                    onChange={(value) => setFormData({ ...formData, contenido: value })}
                    modules={quillModules}
                    className="bg-white"
                    style={{ minHeight: '300px' }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="fas fa-keyboard mr-1"></i>
                  Tip: Usa títulos (H1, H2) para estructurar tu contenido
                </p>
              </CardContent>
            </Card>

            {/* Imagen (Condicional) */}
            {(formData.tipo === 'texto_imagen' || formData.tipo === 'imagen') && (
              <Card className="bg-white shadow-md border-2 border-blue-200">
                <CardContent className="p-6">
                  <Label className="text-lg font-semibold text-gray-800 mb-3 block">
                    <i className="fas fa-image mr-2 text-blue-600"></i>
                    Imagen de la Sección {formData.tipo === 'texto_imagen' ? '(Opcional)' : '(Requerida)'}
                  </Label>
                  
                  {/* Preview de imagen */}
                  {formData.imagen_url && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Vista previa:</p>
                      <img 
                        src={formData.imagen_url.startsWith('http') ? formData.imagen_url : `${API}${formData.imagen_url}`}
                        alt="Preview" 
                        className="rounded-lg shadow-md max-h-48 mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = '<p class="text-red-500 text-sm"><i class="fas fa-exclamation-triangle mr-1"></i>No se puede cargar la imagen. Verifica la URL o sube una nueva.</p>';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    {/* Opción 1: Subir desde PC */}
                    <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <i className="fas fa-upload"></i>
                        Opción 1: Subir imagen desde tu PC
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImagenUpload}
                        className="hidden"
                        id="imagen-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('imagen-upload').click()}
                        disabled={uploadingImagen}
                        className="w-full h-12 text-base border-2 border-dashed border-blue-400 hover:border-blue-600 hover:bg-blue-100"
                      >
                        {uploadingImagen ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Subiendo imagen...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cloud-upload-alt mr-2 text-xl"></i>
                            {formData.imagen_url ? 'Cambiar Imagen' : 'Seleccionar Imagen desde PC'}
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-blue-600 mt-2 text-center">
                        <i className="fas fa-check-circle mr-1"></i>
                        Recomendado: Más rápido y fácil
                      </p>
                    </div>

                    {/* Separador */}
                    <div className="flex items-center gap-3">
                      <hr className="flex-1 border-gray-300" />
                      <span className="text-sm text-gray-500 font-medium">O</span>
                      <hr className="flex-1 border-gray-300" />
                    </div>

                    {/* Opción 2: URL */}
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <i className="fas fa-link"></i>
                        Opción 2: Pegar URL de imagen
                      </p>
                      <Input
                        id="imagen_url"
                        value={formData.imagen_url}
                        onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        className="text-base"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        <i className="fas fa-info-circle mr-1"></i>
                        Usa URLs públicas de servicios como Imgur, Google Drive, etc.
                      </p>
                    </div>

                    {/* Consejos */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800 mb-2">
                        <i className="fas fa-lightbulb mr-2"></i>
                        <strong>Consejos para mejores resultados:</strong>
                      </p>
                      <ul className="text-xs text-green-700 space-y-1 ml-6 list-disc">
                        <li>Tamaño recomendado: 1200x800 píxeles o mayor</li>
                        <li>Formatos: JPG, PNG, WebP (máx 5MB)</li>
                        <li>Usa imágenes con buena iluminación y resolución</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Google Maps (Condicional - Solo para tipo ubicacion) */}
            {formData.tipo === 'ubicacion' && (
              <Card className="bg-white shadow-md">
                <CardContent className="p-6 space-y-4">
                  <Label className="text-lg font-semibold text-gray-800 mb-3 block">
                    <i className="fas fa-map-marked-alt mr-2 text-red-600"></i>
                    Google Maps
                  </Label>
                  
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <i className="fas fa-info-circle"></i>
                      ¿Cómo obtener el código del mapa?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-2 ml-4 list-decimal">
                      <li>Ve a <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Maps</a></li>
                      <li>Busca tu ubicación</li>
                      <li>Click en "Compartir" → "Insertar un mapa"</li>
                      <li>Copia todo el código <code className="bg-blue-200 px-1 rounded">&lt;iframe src="..."&gt;...&lt;/iframe&gt;</code></li>
                      <li>Pégalo en el campo de abajo</li>
                    </ol>
                  </div>

                  <div>
                    <Label htmlFor="mapa_iframe" className="text-sm font-medium text-gray-700 mb-2 block">
                      Código del Mapa (iframe) <span className="text-red-500">*</span>
                    </Label>
                    <textarea
                      id="mapa_iframe"
                      value={formData.mapa_iframe}
                      onChange={(e) => setFormData({ ...formData, mapa_iframe: e.target.value })}
                      placeholder='<iframe src="https://www.google.com/maps/embed?pb=..." width="600" height="450"...</iframe>'
                      className="w-full h-32 p-3 border-2 border-gray-300 rounded-lg font-mono text-xs focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      <i className="fas fa-code mr-1"></i>
                      Pega el código completo del iframe que copiaste de Google Maps
                    </p>
                  </div>

                  {/* Preview del mapa */}
                  {formData.mapa_iframe && formData.mapa_iframe.includes('iframe') && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Vista previa del mapa:</p>
                      <div 
                        className="w-full rounded-lg overflow-hidden border-2 border-gray-300"
                        dangerouslySetInnerHTML={{ __html: formData.mapa_iframe }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Visibilidad */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold text-gray-800 block mb-1">
                      <i className="fas fa-eye mr-2 text-blue-600"></i>
                      Visibilidad en Landing Page
                    </Label>
                    <p className="text-sm text-gray-600">
                      Define si esta sección se mostrará públicamente
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="activo"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded mr-3"
                    />
                    <Label htmlFor="activo" className="text-base font-medium cursor-pointer">
                      {formData.activo ? (
                        <span className="text-green-600">
                          <i className="fas fa-check-circle mr-1"></i>
                          Visible
                        </span>
                      ) : (
                        <span className="text-gray-500">
                          <i className="fas fa-eye-slash mr-1"></i>
                          Oculto
                        </span>
                      )}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Botones de acción */}
            <DialogFooter className="gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="px-6"
              >
                <i className="fas fa-times mr-2"></i>
                Cancelar
              </Button>
              <Button 
                type="submit"
                form="seccion-form"
                className="instituto-gradient text-white px-8"
              >
                <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus-circle'} mr-2`}></i>
                {isEditing ? 'Guardar Cambios' : 'Crear Sección'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionSeccionesLanding;
